import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, KeyRound, LockKeyhole, Mail } from "lucide-react";
import apiClient from "@/services/apiClient";
import ToastHelper from "@/utils/toastHelper";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState("email");
  const [saving, setSaving] = useState(false);

  const sendCode = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await apiClient.post("/StudentAccount/ForgotPassword", { email });
      ToastHelper.success(response.data?.message || "If an account exists, a verification code has been sent.");
      setStep("verify");
    } catch (error) {
      ToastHelper.apiError(error, "Unable to send a verification code.");
    } finally {
      setSaving(false);
    }
  };

  const verifyCode = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await apiClient.post("/StudentAccount/VerifyResetCode", { email, verificationCode });
      ToastHelper.success(response.data?.message || "Verification code accepted.");
      setStep("password");
    } catch (error) {
      ToastHelper.apiError(error, "The verification code is invalid or expired.");
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      ToastHelper.error("Password confirmation does not match.");
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.post("/StudentAccount/ResetPassword", { email, verificationCode, newPassword, confirmPassword });
      ToastHelper.success(response.data?.message || "Password reset successfully.");
      setStep("complete");
    } catch (error) {
      ToastHelper.apiError(error, "Unable to reset password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="wc-card p-8">
        <Link to="/login" className="text-sm text-slate-500 hover:text-[#0A2540] inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to login
        </Link>
        <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">Reset your password</h1>

        {step === "email" && (
          <form onSubmit={sendCode} data-testid="forgot-form">
            <p className="text-sm text-slate-500 mt-1 mb-6">Enter your email and we will send a verification code.</p>
            <label className="wc-label">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input required type="email" className="wc-input pl-9" value={email} onChange={(event) => setEmail(event.target.value)} data-testid="forgot-email" />
            </div>
            <button type="submit" disabled={saving} className="wc-btn-primary w-full justify-center mt-5" data-testid="forgot-submit">{saving ? "Sending..." : "Send verification code"}</button>
          </form>
        )}

        {step === "verify" && (
          <form onSubmit={verifyCode}>
            <p className="text-sm text-slate-500 mt-1 mb-6">Enter the six-digit code sent to {email}.</p>
            <label className="wc-label">Verification code</label>
            <div className="relative">
              <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input required inputMode="numeric" maxLength="6" className="wc-input pl-9" value={verificationCode} onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, ""))} />
            </div>
            <button type="submit" disabled={saving} className="wc-btn-primary w-full justify-center mt-5">{saving ? "Verifying..." : "Verify code"}</button>
            <button type="button" disabled={saving} className="text-sm text-[#0A2540] font-semibold mt-4" onClick={() => setStep("email")}>Use another email</button>
          </form>
        )}

        {step === "password" && (
          <form onSubmit={resetPassword}>
            <p className="text-sm text-slate-500 mt-1 mb-6">Choose a new password with at least 8 characters.</p>
            <label className="wc-label">New password</label>
            <div className="relative mb-4">
              <LockKeyhole className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input required minLength="8" type="password" className="wc-input pl-9" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
            </div>
            <label className="wc-label">Confirm new password</label>
            <div className="relative">
              <LockKeyhole className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input required minLength="8" type="password" className="wc-input pl-9" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
            </div>
            <button type="submit" disabled={saving} className="wc-btn-primary w-full justify-center mt-5">{saving ? "Saving..." : "Reset password"}</button>
          </form>
        )}

        {step === "complete" && (
          <div>
            <p className="text-sm text-slate-600 mt-3 mb-6">Your password has been reset. You can now sign in with your new password.</p>
            <Link to="/login" className="wc-btn-primary w-full justify-center">Go to login</Link>
          </div>
        )}
      </div>
    </div>
  );
}
